import { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import type { Area, Point } from "react-easy-crop";
import { useQueryClient } from "@tanstack/react-query";
import { useUploadHeroImage, useDeleteHeroImage, getGetRecipeQueryKey, getListRecipesQueryKey } from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Upload, Trash2, Loader2, X, Check, ZoomIn } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

async function getCroppedImageBlob(
  imageSrc: string,
  pixelCrop: Area,
  mimeType = "image/jpeg",
  quality = 0.9
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) { reject(new Error("Canvas not supported")); return; }
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Canvas export failed"));
        },
        mimeType,
        quality
      );
    };
    image.onerror = () => reject(new Error("Image load failed"));
    image.src = imageSrc;
  });
}

interface HeroImageUploadDialogProps {
  recipeId: number;
  hasHero: boolean;
  heroUrl: string | null;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
}

export function HeroImageUploadDialog({
  recipeId,
  hasHero,
  heroUrl,
  onSuccess,
  trigger,
}: HeroImageUploadDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"pick" | "crop" | "uploading">("pick");
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const { mutate: uploadHero } = useUploadHeroImage();
  const { mutate: deleteHero, isPending: isDeleting } = useDeleteHeroImage();

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getGetRecipeQueryKey(recipeId) });
    queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
  }, [queryClient, recipeId]);

  const handleFileSelected = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target?.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setStep("crop");
    };
    reader.readAsDataURL(file);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
    e.target.value = "";
  };

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleSave = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setStep("uploading");
    setError(null);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels);
      const file = new File([blob], "hero.jpg", { type: "image/jpeg" });
      uploadHero(
        { id: recipeId, data: { image: file } },
        {
          onSuccess: () => {
            invalidateQueries();
            onSuccess?.();
            setOpen(false);
            resetState();
            toast({ title: "Photo saved!" });
          },
          onError: (err) => {
            setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
            setStep("crop");
          },
        }
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not process image.");
      setStep("crop");
    }
  }, [imageSrc, croppedAreaPixels, recipeId, uploadHero, invalidateQueries, onSuccess, toast]);

  const handleDelete = () => {
    deleteHero(
      { id: recipeId },
      {
        onSuccess: () => {
          invalidateQueries();
          onSuccess?.();
          setOpen(false);
          toast({ title: "Photo removed" });
        },
        onError: (err) => {
          toast({
            title: "Failed to remove photo",
            description: err instanceof Error ? err.message : "Please try again.",
            variant: "destructive",
          });
        },
      }
    );
  };

  const resetState = () => {
    setStep("pick");
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError(null);
  };

  const handleOpenChange = (val: boolean) => {
    setOpen(val);
    if (!val) resetState();
  };

  return (
    <>
      {trigger ? (
        <span
          role="button"
          tabIndex={0}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpen(true); }}
          style={{ display: "contents" }}
          aria-label={hasHero ? "Change hero photo" : "Add a photo"}
        >
          {trigger}
        </span>
      ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={cn(
              "inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl transition-colors",
              hasHero
                ? "text-muted-foreground hover:bg-muted hover:text-foreground"
                : "text-primary hover:bg-primary/10"
            )}
          >
            <Camera className="w-4 h-4" />
            {hasHero ? "Change Photo" : "Add a Photo"}
          </button>

          {hasHero && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              title="Remove photo"
              className="inline-flex items-center justify-center w-8 h-8 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle style={{ fontFamily: "var(--font-display)" }}>
              {hasHero && step === "pick" ? "Change Hero Photo" : "Add a Photo"}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {step === "crop"
                ? "Drag to reposition, pinch or scroll to zoom"
                : "Choose a photo for this recipe"}
            </DialogDescription>
          </DialogHeader>

          {/* Step: pick */}
          {step === "pick" && (
            <div className="px-6 pb-6 space-y-4">
              {hasHero && heroUrl && (
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                  <img
                    src={heroUrl}
                    alt="Current photo"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/25 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <span className="text-white text-sm font-medium">Current photo</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex flex-col items-center gap-2 px-4 py-5 rounded-2xl bg-primary/5 hover:bg-primary/10 border border-primary/20 transition-colors group"
                >
                  <Camera className="w-7 h-7 text-primary group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-medium text-foreground">Take Photo</span>
                  <span className="text-xs text-muted-foreground">Use camera</span>
                </button>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center gap-2 px-4 py-5 rounded-2xl bg-muted hover:bg-muted/80 border border-border/60 transition-colors group"
                >
                  <Upload className="w-7 h-7 text-muted-foreground group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-medium text-foreground">Browse File</span>
                  <span className="text-xs text-muted-foreground">JPG, PNG, WebP</span>
                </button>
              </div>

              {error && (
                <p className="text-sm text-destructive bg-destructive/10 rounded-xl px-3 py-2">{error}</p>
              )}
            </div>
          )}

          {/* Step: crop */}
          {step === "crop" && imageSrc && (
            <div className="space-y-4 px-6 pb-6">
              {/* Crop viewport */}
              <div className="relative w-full overflow-hidden rounded-xl bg-black" style={{ height: 320 }}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={4 / 3}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  showGrid
                  style={{
                    containerStyle: { borderRadius: "0.75rem" },
                  }}
                />
              </div>

              {/* Zoom slider */}
              <div className="flex items-center gap-3">
                <ZoomIn className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 accent-primary"
                />
                <span className="text-xs text-muted-foreground w-8 text-right">
                  {zoom.toFixed(1)}×
                </span>
              </div>

              {error && (
                <p className="text-sm text-destructive bg-destructive/10 rounded-xl px-3 py-2">{error}</p>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setStep("pick"); setImageSrc(null); setError(null); }}
                  className="rounded-xl"
                >
                  <X className="w-4 h-4 mr-1.5" />
                  Back
                </Button>
                <Button
                  onClick={handleSave}
                  className="flex-1 rounded-xl gap-2"
                >
                  <Check className="w-4 h-4" />
                  Save Photo
                </Button>
              </div>
            </div>
          )}

          {/* Step: uploading */}
          {step === "uploading" && (
            <div className="px-6 pb-8 flex flex-col items-center gap-4 py-8">
              <div className="bg-primary/10 rounded-full p-4">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <p className="text-sm text-muted-foreground">Uploading your photo…</p>
            </div>
          )}

          {/* Hidden file inputs */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleInputChange}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            className="hidden"
            onChange={handleInputChange}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
